<template>
  <main class="products-page">
    <div class="products-page__body" ref="body">
      <div class="products-page__row">
        <h1 class="products-page__title">Товары</h1>
        <button
          class="products-page__buttons-add products-page__buttons-add--product"
          type="button"
          v-on:click="openProductDialogForm">
          Добавить
        </button>
      </div>
      <div class="products-page__search">
        <input-text-search
          class="products-page__search"
          v-bind:value="searchQuery"
          v-on:input="onInputSearchQuery"
          placeholder="Введите название товара"
        />
      </div>
      <div>
        <modal-dialog
          persistent
          with-cross
          title=""
          v-model="isShowNotificationOfTransferBalance"
          v-on:input="onCloseNotificationOfTransferBalance"
        >
          <template v-slot:header>
            <p>
              Остатки товара переехали в
              <router-link v-bind:to="{ name: 'ClinicPanelReportWarehouseProductBalances' }">
                Отчет
              </router-link>
              по складу
            </p>
          </template>
        </modal-dialog>
      </div>
      <div class="products products__content">
        <div class="products__categories">
          <div class="products__categories__caption">Категория</div>
          <div class="products__categories__container">
            <category-item
              v-for="item in categories"
              has-edit
              has-delete
              v-bind:category="item"
              v-bind:key="item.id"
              v-bind:selected-category="selectedCategories[0] || -1"
              v-on:select-category="onSelectingCategory(item.id)"
              v-on:edit-category="onShowEditDialogCategory"
              v-on:delete-category="onDeleteCategory"
            />
          </div>
        </div>
        <div class="products__table">
          <product-table
            v-bind:product-list="productList"
            v-bind:is-await-request="isAwaitRequest"
            v-bind:sort="sort"
            v-on:delete-product="onDeleteProduct"
            v-on:edit-product="fetchProduct"
            v-on:sort-product-list="onSortProductList"
          >
            <template v-slot:empty-result>
              <p v-if="hasSearchQuery">Товаров с таким наименованием не найдено</p>
              <div v-else-if="isEmptyCategories" class="products-page__empty-category">
                <h3 class="products-page__empty-category__title">У вас пока нет категорий</h3>
                <button
                  class="products-page__buttons-add empty-category"
                  type="button"
                  v-on:click="onAddCategory"
                >Добавить категорию</button>
              </div>
              <button
                v-else
                class="products-page__buttons-add empty"
                type="button"
                v-on:click="onAddProduct"
              >Добавить товар</button>
            </template>
          </product-table>
        </div>
      </div>
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-bind:label="productFormTitle"
        v-if="showSideForm"
        v-on:cancel="closeProductDialogForm">
        <input-select
          v-if="showSideFormTypeSelector"
          class="products-dialog__input-select"
          title="Тип"
          v-bind:options="formTypes"
          v-model="selectedForm"
        />
        <form-product
          v-if="selectedForm === $options.productForm"
          v-bind:category-options="categories"
          v-bind:unit-options="unitOptions"
          v-bind:vendor-options="vendorOptions"
          v-bind:product-id="selectedProductId"
          v-bind:initial-name="selectedProduct.name"
          v-bind:initial-category="selectedProduct.category"
          v-bind:initial-manufacturer="selectedProduct.manufacturer"
          v-bind:initial-unit="selectedProduct.unit"
          v-bind:initial-vendor="selectedProduct.vendor"
          v-bind:initial-count-in-pack="selectedProduct.countInPack"
          v-bind:initial-critical-stock-level="selectedProduct.criticalStockLevel"
          v-bind:input-errors="inputErrors"
          v-bind:is-await-request="isAwaitProductRequest"
          v-on:create-unit="openUnitForm"
          v-on:submit="onSubmitProduct"
          v-on:close="closeProductDialogForm" />
        <form-product-category
          v-if="selectedForm === $options.formCategory"
          v-bind:category-id="category.id"
          v-bind:initial-name="category.name"
          v-bind:input-errors="inputErrors"
          v-bind:is-await-request="isAwaitProductRequest"
          v-on:submit="onSubmitCategory"
          v-on:close="closeProductDialogForm" />
      </confirm-side-dialog>
    </side-slide-transition>
    <modal-dialog
      content-class="products-page__modal"
      v-bind:title="dialogTitle"
      v-model="showModalDialog"
      v-on:input="closeModalDialog"
      with-cross
    >
      <form-product-unit
        v-if="formName === $options.formUnit"
        v-bind:input-errors="inputErrors"
        v-bind:unit-id="unit.id"
        v-bind:initial-name="unit.name"
        v-on:submit="onSubmitUnit"
        v-on:close="closeModalDialog" />
    </modal-dialog>
    <modal-dialog
      with-cross
      v-model="showProductWriteOffDialog"
      :title="removeProductTitle"
    >
      <span class="v-card__text__description">{{ removeProductMessage }}</span>
      <router-link
        class="products-page__button-cash-out"
        v-bind:to="{ name: 'ClinicPanelWarehouseProductWriteOffList' }"
      >К товарам</router-link>
    </modal-dialog>
  </main>
</template>

<script>
import { Product, ProductCategory, ProductUnit } from '@/storage/gateways/ProductGateway';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import ProductTable from '@/components/Warehouse/ProductTable.vue';
import ThePagination from '@/components/ThePagination.vue';
import FormProduct from '@/components/Warehouse/FormProduct.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import FormProductCategory from '@/components/Warehouse/FormProductCategory.vue';
import FormProductUnit from '@/components/Warehouse/FormProductUnit.vue';
import InputTextSearch from '@/components/InputTextSearch.vue';
import debounce from 'lodash.debounce';
import CategoryItem from '@/components/CategoryItem.vue';
import InputSelect from '@/components/InputSelect.vue';

/**
 * @description Примесь единиц измерения товаров.
 * @type {any}
 */
const unitMixin = {
  formUnit: 'unit',
  data() {
    return {
      unit: new ProductUnit(),
    };
  },
  methods: {
    /**
     * @description Открыть форму для работы с единицей измерения.
     * @returns {void}
     */
    async openUnitForm() {
      this.formName = this.$options.formUnit;
      this.openModalDialog();
    },
    /**
     * @description Обработать отправку единицы измерения.
     * @param {ProductUnitRequest} request
     */
    onSubmitUnit(request) {
      this.createProductUnit(request.toQuery());
    },
    /**
     * @description Создать единицу измерения товара.
     * @param {Object} params
     */
    createProductUnit(params) {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.closeModalDialog();
        this.fetchOptions().then(() => {
          this.selectedProduct.unit = response.unit_id;
        });
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .createProductUnit(params)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
};

/**
 * @description Примесь категорий товаров.
 * @type {any}
 */
const categoryMixin = {
  formCategory: 'category',
  data() {
    return {
      category: new ProductCategory(),
    };
  },
  methods: {
    /**
     * @description Обработать выбор категорий.
     * @param {array} categories
     */
    onSelectingCategory(categories) {
      this.page = 1;
      this.selectedCategories = [categories];
      this.fetchProductList(this.sort, this.selectedCategories);
    },
    /**
     * @description Обработать удаление категории.
     * @param {string} categoryId
     */
    async onDeleteCategory(categoryId) {
      const dialog = await this
        .showRemoveAccentDialog('При удалении категории все товары в\u00A0ней будут удалены, продолжить?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchProductList(this.sort, this.selectedCategories);
        this.fetchOptions();
      };
      const onFail = (error) => {
        if (error.has('count-in-warehouse')) {
          this.removeProductMessage = error.getTitle();
          this.removeProductTitle = 'Удаление категории';
          this.showProductWriteOffDialog = true;
        }
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .deleteProductCategory(categoryId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку категории.
     * @param {ProductCategoryRequest} request
     */
    onSubmitCategory(request) {
      if (request.getId()) {
        this.editProductCategory(request);
      } else {
        this.createProductCategory(request);
      }
    },
    /**
     * @description Создать категорию товара.
     * @param {ProductCategoryRequest} request
     */
    createProductCategory(request) {
      this.isAwaitProductRequest = true;
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.closeProductDialogForm();
        this.fetchOptions().then(() => {
          this.selectedProduct.category = response.category_id;
          this.onSelectingCategory(response.category_id);
        });
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .createProductCategory(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitProductRequest = false; });
    },
    /**
     * @description Изменить категорию товара.
     * @param {ProductCategoryRequest} request
     */
    editProductCategory(request) {
      this.isAwaitProductRequest = true;
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.inputErrors = {};
        this.closeProductDialogForm();
        this.fetchOptions();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .editProductCategory(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitProductRequest = false; });
    },
    /**
     * @description Обработать показ диалогового окна категории.
     * @param {number} categoryId
     * @returns {void}
     */
    onShowEditDialogCategory(categoryId) {
      const category = this.categories.find((item) => item.id === categoryId);
      this.category = new ProductCategory(category.id, category.name);
      this.showSideFormTypeSelector = false;
      this.selectedForm = this.$options.formCategory;
      this.showSideForm = true;
      this.inputErrors = {};
    },
    onAddCategory() {
      this.showSideFormTypeSelector = false;
      this.category = new ProductCategory();
      this.selectedForm = this.$options.formCategory;
      this.showSideForm = true;
      this.inputErrors = {};
    },
  },
};

/**
 * @description Примесь уведомления о переносе остатка.
 * @type {any}
 */
const notificationOfTransferBalanceMixin = {
  data() {
    return {
      isShowNotificationOfTransferBalance: false,
    };
  },
  methods: {
    onCloseNotificationOfTransferBalance() {
      this
        .$notificationGateway
        .markNotificationOfTransferProductBalanceAsViewed()
        .finally(() => { this.isShowNotificationOfTransferBalance = false; });
    },
    showNotificationOfTransferBalanceIfNeed() {
      if (this.actions.includes('view-notification-of-product-balance')) {
        this.isShowNotificationOfTransferBalance = true;
      }
    },
  },
};

/**
 * @description Страница списка товаров.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseProductList',
  components: {
    InputSelect,
    CategoryItem,
    InputTextSearch,
    FormProductUnit,
    FormProductCategory,
    ModalDialog,
    ConfirmSideDialog,
    FormProduct,
    SideSlideTransition,
    ProductTable,
    ThePagination,
  },
  productForm: 'productForm',
  computed: {
    hasSearchQuery() {
      return this.searchQuery.length > 0;
    },
    dialogTitle() {
      if (this.formName === this.$options.formCategory) {
        return this.category.id !== null ? 'Редактирование категории' : 'Добавление категории';
      }
      return 'Добавление единицы измерения';
    },
    /**
     * @description Заголовок формы товар.
     * @returns {string}
     */
    productFormTitle() {
      if (this.selectedForm === this.$options.productForm) {
        return this.selectedProductId ? 'Редактировать товар'
          : 'Добавить товар';
      }
      if (this.selectedForm === this.$options.formCategory) {
        return this.category.id ? 'Редактировать категорию' : 'Добавить категорию';
      }

      return 'Добавить';
    },
    isEmptyCategories() {
      return this.categories.length === 0;
    },
  },
  created() {
    this.fetchOptions().then(() => {
      this.selectedCategories = this.categories.slice(0, 1).map(({ id }) => id);
      this
        .fetchProductList({ type: 'name', order: 'asc' }, this.selectedCategories)
        .finally(() => {
          this.showNotificationOfTransferBalanceIfNeed();
        });
    });
  },
  data() {
    return {
      actions: [],
      showSideForm: false,
      formTypes: [
        {
          title: 'Категория',
          value: this.$options.formCategory,
        },
        {
          title: 'Товар',
          value: this.$options.productForm,
        },
      ],
      showSideFormTypeSelector: false,
      selectedForm: null,
      productList: [],
      isAwaitRequest: true,
      isAwaitProductRequest: false,
      sort: {},
      page: 1,
      pageCount: 0,
      formName: '',
      searchQuery: '',
      selectedProductId: '',
      selectedProduct: new Product(),
      selectedCategories: [],
      inputErrors: {},
      showProductDialogForm: false,
      isAwaitDownloadRequest: false,
      categories: [],
      unitOptions: [],
      vendorOptions: [],
      showModalDialog: false,
      removeProductMessage: '',
      removeProductTitle: '',
      showProductWriteOffDialog: false,
    };
  },
  methods: {
    onAddProduct() {
      this.showSideFormTypeSelector = false;
      this.selectedForm = this.$options.productForm;
      this.selectedProduct.category = this.selectedCategories[0] || '';
      this.showSideForm = true;
      this.inputErrors = {};
    },
    /**
     * @description Создать товар.
     * @param {ProductRequest} request
     */
    createProduct(request) {
      this.isAwaitProductRequest = true;
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.selectedCategories = [request.getCategoryId()];
        this.closeProductDialogForm();
        this.inputErrors = {};
        this.fetchProductList(this.sort, this.selectedCategories);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .create(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitProductRequest = false; });
    },
    /**
     * @description Закрыть диалоговое окно формы товара.
     * @returns {void}
     */
    closeProductDialogForm() {
      this.selectedProductId = '';
      this.selectedProduct = new Product();
      this.category = new ProductCategory();
      this.selectedForm = null;
      this.showSideForm = false;
    },
    /**
     * @description Редактировать товар.
     * @param {ProductRequest} request
     */
    editProduct(request) {
      this.isAwaitProductRequest = true;
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.selectedCategories = [request.getCategoryId()];
        this.closeProductDialogForm();
        this.inputErrors = {};
        this.fetchProductList(this.sort, this.selectedCategories);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .edit(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitProductRequest = false; });
    },
    /**
     * @description Открыть диалоговое окно формы товаров.
     * @returns {void}
     */
    openProductDialogForm(showSideFormTypeSelector = true) {
      this.showSideFormTypeSelector = showSideFormTypeSelector;
      this.showSideForm = true;
      this.inputErrors = {};
    },
    /**
     * @description Открыть модальное диалоговое окно.
     * @returns {void}
     */
    openModalDialog() {
      this.showModalDialog = true;
      this.inputErrors = {};
    },
    /**
     * @description Обработать ввод запроса.
     * @type {string} value
     * @returns {void}
     */
    onInputSearchQuery(value) {
      this.searchQuery = value;
    },
    /**
     * @description Закрыть модальное диалоговое окно.
     * @returns {void}
     */
    closeModalDialog() {
      this.formName = '';
      this.showModalDialog = false;
      this.category = new ProductCategory();
      this.unit = new ProductUnit();
    },
    /**
     * @description Получить список товаров.
     * @param {Object} sort        Сортировка.
     * @param {any[]}  categoryIds Идентификаторы категорий.
     * @returns {Promise<void>}
     */
    async fetchProductList(sort, categoryIds = []) {
      this.isAwaitRequest = true;
      this.sort = sort;
      this.selectedCategories = categoryIds;
      const onSuccess = ({
        actions, categories, products, pageCount,
      }) => {
        this.actions = actions;
        this.categories = categories;
        this.productList = products;
        this.pageCount = pageCount;
        this.defineCategory(products, categories, categoryIds, sort);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      const either = await this.$productGateway.getAll(
        this.sort,
        this.page,
        this.selectedCategories,
        this.searchQuery,
      );
      either.fold(onFail, onSuccess);
      this.isAwaitRequest = false;
    },
    /**
     * @description Определить категорию при поиске.
     * @param {Array<Object>} products    Товары
     * @param {Array<Object>} categories  Категории
     * @param {Array<string>} categoryIds Идентификаторы категорий
     * @param {Object}        sort        Сортировка.
     */
    defineCategory(products, categories, categoryIds, sort) {
      const productNotFound = this.hasSearchQuery && products.length === 0 && categories.length > 0;
      if (productNotFound) {
        const firstCategoryId = categories[0].id;
        const isCurrentCategoryInResults = categoryIds.some(
          (id) => categories.find((cat) => cat.id === id),
        );
        if (!isCurrentCategoryInResults) {
          this.selectedCategories = [firstCategoryId];
          this.fetchProductList(sort, this.selectedCategories);
        }
      }
    },
    /**
     * @description Получить товар.
     * @param {string} productId Идентификатор товар.
     */
    async fetchProduct(productId) {
      const onSuccess = ({ product }) => {
        this.selectedProductId = productId;
        this.selectedProduct = product;
        this.selectedForm = this.$options.productForm;
        this.openProductDialogForm(false);
      };
      const onFail = (error) => {
        this.selectedProductId = '';
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .viewProduct(productId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить опции.
     * @returns {Promise}
     */
    fetchOptions() {
      const onSuccess = ({ categories, units, vendors }) => {
        this.categories = categories;
        this.unitOptions = units;
        this.vendorOptions = vendors;
      };
      const onFail = (error) => {
        this.selectedProductId = '';
        this.notifyFail(error.getTitle());
      };
      return this
        .$productGateway
        .viewOptions()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку запроса товар.
     * @param {ProductRequest} request
     */
    onSubmitProduct(request) {
      if (request.getId()) {
        this.editProduct(request);
      } else {
        this.createProduct(request);
      }
    },
    /**
     * @description Обработать событие удаления товар.
     * @param {number} id Идентификатор товар.
     * @returns {void}
     */
    async onDeleteProduct(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить товар?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchProductList(this.sort, this.selectedCategories);
      };
      const onFail = (error) => {
        if (error.has('count-in-warehouse')) {
          this.removeProductMessage = error.getTitle();
          this.removeProductTitle = 'Удаление товара';
          this.showProductWriteOffDialog = true;
        }
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .delete(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
    onSortProductList(sort) {
      this.fetchProductList(sort, this.selectedCategories);
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
    categoryMixin,
    notificationOfTransferBalanceMixin,
    unitMixin,
  ],
  watch: {
    searchQuery: {
      handler: debounce(async function _() {
        this.page = 1;
        await this.fetchProductList({ type: 'name', order: 'asc' }, this.selectedCategories);
      }, 300),
    },
    page: {
      handler: debounce(function _() {
        this.fetchProductList(this.sort, this.selectedCategories);
      }),
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.products-page__modal {
  .v-card__title {
    margin-inline: auto;
    margin-bottom: 10px;
    max-width: 400px;
    word-break: auto-phrase;
  }
  .v-card__text {
    display: grid;
    gap: 15px;
    &__description {
      font-size: 16px;
    }
  }
}

.products-dialog {
  &__input-select {
    margin-top: 20px;
  }
}

.form-category {
  position: relative;
  display: grid;
  gap: 0.75rem;
  padding-block: 20px;
  &__buttons {
    display: grid;
    gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 0 0 0 auto;
  }
}

.products {
  &__content {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 10px;
  }
  &__categories {
    width: 280px;
    &__caption {
      font-size: 14px;
      line-height: 17px;
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 13px 10px 11px;
    }
    &__container {
      display: flex;
      flex-direction: column;
      gap: 15px;
      background: #f2f7fa;
      padding: 20px 10px;
    }
  }
  &__table {
    width: 100%;
    overflow: auto;
  }
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__search {
      max-width: 380px;
      &__caption {
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        border-bottom: 2px solid #0489e0;
        padding: 10px;
      }
      &__container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 280px;
        background: #f2f7fa;
        padding: 20px 10px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr;
      max-width: 200px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      color: #fff;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        @include buttons.btn-blue(100%, 48px, 16px);
        max-width: 200px;

        &.empty {
          flex-shrink: 0;
          width: fit-content;
          padding-inline: 32px;
        }

        &.empty-category {
          flex-shrink: 0;
          width: fit-content;
          padding: 22px;
          max-width: 350px;
          font-size: 1rem;
        }
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        @include buttons.btn-blue(50%, 48px, 16px);
        width: 100%;
      }

      &-delete:disabled {
        background-color: var(--unfocus-color);
      }
    }

    &__button-cash-out {
      @include buttons.btn-blue(50%, 48px, 16px);
      width: 100%;
      color: white !important;
      text-decoration: none;
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__empty-category {
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 18px;
      &__title {
        font-size: inherit;
        font-weight: 400;
      }
    }

    &__search {
      margin-bottom: 20px;
      max-width: 100%;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 15px 30px;
      margin-bottom: 10px;
    }
  }
}

@media screen and (max-width: 960px) {
  .products {
    &-page {
      flex-direction: column;

      &__row {
        flex-direction: column;
      }
      &__empty-category {
        margin-top: 10px;

        &__description {
          font-size: 1rem;

          &__add-icon {
            &:before {
              width: 1rem;
              height: 1rem;
              font-size: 1rem;
              line-height: 1rem;
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .products {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }

      &__buttons {
        grid-template-columns: 1.2fr 1fr;
        max-width: 100%;
        row-gap: 10px;
      }
    }
    &__content {
      grid-template-columns: 1fr;
    }
    &__categories {
      width: 100%;
    }
  }
}

@media screen and (max-width: 480px) {
  .products-page {
    &__buttons {
      grid-template-columns: minmax(250px, 300px);
    }
    &__buttons-add {
      &--product-category {
        order: 1;
      }
      &--product {
        order: 0;
      }
    }
    & &__empty-category {
      &__description {
        max-width: 240px;
        margin: 0;
      }
    }
  }
}
</style>
